<template>
  <div
    v-if="bannerData && isVisible"
    data-qa="award_banner"
    class="award-banner"
    :class="{
      'award-banner--dark': bannerData.isDarkMode,
      'ma-2': isMobile,
      'mb-6': !isMobile,
    }"
    @click="handleShowMore"
  >
    <div
      class="d-flex align-center"
      :class="isMobile ? 'px-2 pt-1 mb-2' : 'mr-8'"
    >
      <img
        alt="Золотая звезда"
        height="32"
        width="32"
        class="flex-grow-0"
        :class="isMobile ? 'mr-6' : 'mr-8'"
        :src="starIcon"
      >
      <div class="text-body-1">
        {{ bannerData.banner.title }}
      </div>
    </div>
    <div
      class="ml-auto"
      :class="{ 'mr-2': !isMobile }"
    >
      <v-btn
        text
        class="px-2 mr-2"
        :dark="bannerData.isDarkMode"
        @click.stop="handleClose"
      >
        Скрыть
      </v-btn>
      <v-btn
        text
        class="px-2"
        :dark="bannerData.isDarkMode"
        :color="bannerData.isDarkMode ? 'gold' : 'primary'"
        @click.stop="handleShowMore"
      >
        {{ bannerData.banner.button.moreText }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue'
import Cookies from 'js-cookie'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import getCurrentMoscowDate from 'src/components/common/AwardBanner/functions/getCurrentMoscowDate'
import {
  AWARD_BANNER_COOKIE,
  AWARD_BANNER_COOKIE_TTL_DAYS,
  AWARD_BANNER_VISIBLE_FROM,
  AWARD_BANNER_VISIBLE_UNTIL,
  AWARD_CONTENT_BY_DATE,
  AWARD_EVENT_DATES,
} from 'src/components/common/AwardBanner/constants'

/**
 * Баннер премии ПроДокторов.
 *
 * Аналог `www/vue_components/common/AwardBanner` для modern Vue SSR-страниц.
 * Показ зависит от `site_settings.show_award_banners`, страны, дат события и cookie.
 */
const { context }: CommonContextStore = useContextStore()
const isMobile = useIsMobile()
const isCookieVisible = ref(true)

const requestCookies = computed(() => {
  const { COOKIES: cookies } = context.request

  return cookies && typeof cookies === 'object' ? cookies : {}
})

const bannerData = computed(() => {
  const currentMoscowDate = getCurrentMoscowDate()
  const lastDate = AWARD_EVENT_DATES[AWARD_EVENT_DATES.length - 1]
  let currentDateIndex = AWARD_EVENT_DATES.findIndex(date => currentMoscowDate <= date) - 1

  if (currentMoscowDate >= lastDate) {
    currentDateIndex = AWARD_EVENT_DATES.length - 1
  }

  if (currentDateIndex < 0) {
    return null
  }

  return AWARD_CONTENT_BY_DATE[currentDateIndex] ?? null
})

const starIcon = computed(() => {
  const staticPath = context.global?.static?.src || '/static/'
  const file = bannerData.value?.isDarkMode
    ? '_v1/pd/icons/stars/star-gold-outlined.svg'
    : '_v1/pd/icons/stars/star-gold-filled.svg'

  return `${staticPath}${file}`
})

const isInDateWindow = computed(() => {
  const today = new Date()

  return today > AWARD_BANNER_VISIBLE_FROM && !(today > AWARD_BANNER_VISIBLE_UNTIL)
})

const isVisible = computed(() => (
  isCookieVisible.value
  && isInDateWindow.value
  && Boolean(context.site_settings?.show_award_banners)
  && Boolean(context.request?.country?.is_russia)
  && Boolean(bannerData.value)
  && requestCookies.value[AWARD_BANNER_COOKIE] !== 'false'
))

onMounted(() => {
  try {
    const raw = Cookies.get(AWARD_BANNER_COOKIE)

    if (raw === undefined) {
      Cookies.set(AWARD_BANNER_COOKIE, 'true', {
        expires: AWARD_BANNER_COOKIE_TTL_DAYS,
        path: '/',
      })
      isCookieVisible.value = true

      return
    }

    isCookieVisible.value = JSON.parse(raw)
  } catch {
    isCookieVisible.value = true
  }
})

function handleClose() {
  isCookieVisible.value = false
  Cookies.set(AWARD_BANNER_COOKIE, 'false', {
    expires: AWARD_BANNER_COOKIE_TTL_DAYS,
    path: '/',
  })
}

function handleShowMore() {
  const link = bannerData.value?.banner.button.moreLink

  if (link && process.env.IS_CLIENT) {
    window.open(link, '_blank')
  }
}
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.award-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-radius: $border-radius-md;
  border: 1px solid $new-color-gold;
  background-color: $ui-kit-bg-gray-0;
  color: $ui-kit-text;
  padding: 12px 8px;
  cursor: pointer;

  &--dark {
    border-color: $ui-kit-text;
    background-color: $ui-kit-text;
    color: $ui-kit-bg-gray-0;
  }
}
</style>
